/**
 * Detects the account-allowlist rejection that private stacks return.
 *
 * The APIs set ALLOWED_EMAILS in restricted environments (QA) and answer every
 * request from an account outside that list with 403 and a fixed detail message.
 * Production leaves ALLOWED_EMAILS unset, so none of this ever triggers there and
 * the same bundle can ship to both environments.
 *
 * Detection matches the message rather than a custom response header because the
 * APIs are served from a different origin and CORS would not expose one without
 * also changing every backend.
 */
import { clearTokenSession, decodeJwtPayload, getCookieToken } from "./authSession";
import { clearRunnerUrl } from "../Config";
import { clearSelectedCluster } from "./clusterSwitch";

const RESTRICTED_PATTERN = /restricted to authorized accounts/i;

export const REJECTED_EMAIL_KEY = "runner_rejected_email";
export const NOT_AUTHORIZED_PATH = "/not-authorized";

export function isRestrictedDetail(detail) {
  return typeof detail === "string" && RESTRICTED_PATTERN.test(detail);
}

/**
 * True when a response is an allowlist rejection. Reads a clone so the caller can
 * still consume the original body.
 */
export async function isRestrictedResponse(response) {
  if (!response || response.status !== 403) return false;
  try {
    const data = await response.clone().json();
    return isRestrictedDetail(data?.detail);
  } catch {
    return false;
  }
}

/** Email of the signed-in account, read from the token we already hold. */
export function emailFromCookieToken() {
  const payload = decodeJwtPayload(getCookieToken());
  return (payload?.email || payload?.["cognito:username"] || "").trim();
}

/**
 * Drop every trace of the session so a rejected account cannot keep browsing, and
 * remember the address so the not-authorized page can name it.
 */
export function clearRestrictedSession(email) {
  const rejected = (email || "").trim();
  clearTokenSession();
  try { clearRunnerUrl(); } catch { /* ignore */ }
  try { clearSelectedCluster(); } catch { /* ignore */ }
  try {
    if (rejected) sessionStorage.setItem(REJECTED_EMAIL_KEY, rejected);
  } catch { /* ignore */ }
}

export function readRejectedEmail() {
  try {
    return sessionStorage.getItem(REJECTED_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

export function clearRejectedEmail() {
  try {
    sessionStorage.removeItem(REJECTED_EMAIL_KEY);
  } catch { /* ignore */ }
}

let redirecting = false;

/**
 * Hard-redirect to the not-authorized page. Used from non-React modules where the
 * router is not reachable. Guarded so a burst of parallel 403s redirects once.
 */
export function redirectToNotAuthorized(email) {
  if (redirecting) return;
  redirecting = true;
  clearRestrictedSession(email);
  if (typeof window !== "undefined" && window.location.pathname !== NOT_AUTHORIZED_PATH) {
    window.location.replace(NOT_AUTHORIZED_PATH);
  }
}
